/**
 * PremisesReviewWizard
 *
 * 3-step wizard for premises "Submit for Internal Review" flow.
 * Step 0: Choose LOR, Step 1: Select certified mail recipient, Step 2: Confirm & submit.
 */
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { formatMailingAddress } from '@/components/coverage/propertyContactAddress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { RadioGroup } from '@/components/ui/radio-group';
import { StepDialog } from '@/components/ui/step-dialog';
import type { PropertyContact } from '@/services/types/coverage-types';

import type { MatterDocLite } from '../types';

const getContactLabel = (contact: PropertyContact): string => {
  if (contact.name) return contact.name;
  if (contact.contact_type === 'PROPERTY_OWNER') return 'Property Owner';
  if (contact.contact_type === 'PROPERTY_MANAGER') return 'Property Manager';
  return contact.custom_type_label || 'Contact';
};

const getContactTypeLabel = (contact: PropertyContact): string => {
  if (contact.contact_type === 'PROPERTY_OWNER') return 'Property Owner';
  if (contact.contact_type === 'PROPERTY_MANAGER') return 'Property Manager';
  return contact.custom_type_label || 'Other';
};

const WIZARD_STEPS = [
  { id: 'choose-lor', label: 'Choose LOR' },
  { id: 'select-recipient', label: 'Select Recipient' },
  { id: 'confirm-submit', label: 'Confirm & Submit' },
] as const;

const STEP_DESCRIPTIONS: Record<number, string> = {
  0: 'Pick the LOR the managing paralegal should review for this premises coverage.',
  1: 'Choose the property contact the reviewer should send certified mail to.',
  2: 'Review your selections and submit for internal review.',
};

interface PremisesReviewWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lorDocs: MatterDocLite[];
  currentCoverageLorDocs: MatterDocLite[];
  otherMatterLorDocs: MatterDocLite[];
  propertyContacts: PropertyContact[];
  isSubmitting: boolean;
  defaultLorSelection: string | null;
  renderLorOptionCard: (
    doc: MatterDocLite,
    options?: { highlightCurrentCoverage?: boolean },
  ) => React.ReactNode;
  onSubmit: (lorId: string, contactIdx: number) => Promise<void>;
}

export const PremisesReviewWizard: React.FC<PremisesReviewWizardProps> = ({
  open,
  onOpenChange,
  lorDocs,
  currentCoverageLorDocs,
  otherMatterLorDocs,
  propertyContacts,
  isSubmitting,
  defaultLorSelection,
  renderLorOptionCard,
  onSubmit,
}) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [completedSteps, setCompletedSteps] = React.useState<Set<number>>(
    new Set(),
  );
  const [selectedLorId, setSelectedLorId] = React.useState<string | null>(null);
  const [selectedContactIdx, setSelectedContactIdx] = React.useState<
    number | null
  >(null);
  const [isOtherMatterLorsOpen, setIsOtherMatterLorsOpen] =
    React.useState(false);

  const prevOpen = React.useRef(open);

  React.useEffect(() => {
    if (open && !prevOpen.current) {
      // Opening: initialize
      setCurrentStep(0);
      setCompletedSteps(new Set());
      setSelectedLorId(defaultLorSelection);
      setIsOtherMatterLorsOpen(
        currentCoverageLorDocs.length === 0 && otherMatterLorDocs.length > 0,
      );
      const firstMailableIdx = propertyContacts.findIndex((c) =>
        Boolean(c.mailing_address),
      );
      setSelectedContactIdx(firstMailableIdx >= 0 ? firstMailableIdx : null);
    } else if (!open && prevOpen.current) {
      // Closing: reset selection state
      setSelectedLorId(null);
      setSelectedContactIdx(null);
      setIsOtherMatterLorsOpen(false);
    }
    prevOpen.current = open;
  }, [open, defaultLorSelection, currentCoverageLorDocs, otherMatterLorDocs, propertyContacts]);

  const handleContinue = () => {
    if (currentStep === 0) {
      if (!selectedLorId) {
        toast.error('Select a Letter of Representation to continue.');
        return;
      }
      setCompletedSteps((prev) => new Set([...prev, 0]));
      setCurrentStep(1);
    } else if (currentStep === 1) {
      if (selectedContactIdx === null) {
        toast.error('Select a property contact with a mailing address.');
        return;
      }
      const contact = propertyContacts[selectedContactIdx];
      if (!contact?.mailing_address) {
        toast.error('The selected property contact needs a mailing address.');
        return;
      }
      setCompletedSteps((prev) => new Set([...prev, 1]));
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (completedSteps.has(stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLorId || selectedContactIdx === null) return;
    try {
      await onSubmit(selectedLorId, selectedContactIdx);
    } catch {
      // Error handled by parent
    }
  };

  const selectedLorDoc = lorDocs.find((d) => d.id === selectedLorId);
  const selectedContact =
    selectedContactIdx !== null ? propertyContacts[selectedContactIdx] : null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <RadioGroup
            value={selectedLorId ?? ''}
            onValueChange={(val) => setSelectedLorId(val)}
            className="space-y-4"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-zinc-900">
                  This coverage
                </div>
                <Badge
                  variant="outline"
                  className="border-zinc-200 text-zinc-600"
                >
                  {currentCoverageLorDocs.length}
                </Badge>
              </div>
              {currentCoverageLorDocs.length > 0 ? (
                <div className="space-y-2">
                  {currentCoverageLorDocs.map((doc) =>
                    renderLorOptionCard(doc, {
                      highlightCurrentCoverage: true,
                    }),
                  )}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-500">
                  No generated LORs are linked to this coverage yet. You can
                  still choose another matter LOR below if needed.
                </div>
              )}
            </div>
            {otherMatterLorDocs.length > 0 ? (
              <Collapsible
                open={isOtherMatterLorsOpen}
                onOpenChange={setIsOtherMatterLorsOpen}
              >
                <div className="rounded-xl border border-zinc-200 bg-white">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-3 text-left"
                    >
                      <div className="flex items-center gap-2">
                        {isOtherMatterLorsOpen ? (
                          <ChevronDown className="h-4 w-4 text-zinc-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-zinc-500" />
                        )}
                        <span className="text-sm font-semibold text-zinc-900">
                          Other matter LORs
                        </span>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-zinc-200 text-zinc-600"
                      >
                        {otherMatterLorDocs.length}
                      </Badge>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 border-t border-zinc-100 px-4 py-3">
                    {otherMatterLorDocs.map((doc) => renderLorOptionCard(doc))}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ) : null}
          </RadioGroup>
        );

      case 1:
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-zinc-700">
              Certified mail recipient
            </p>
            <div className="space-y-3">
              {propertyContacts.map((contact, idx) => {
                const isSelected = selectedContactIdx === idx;
                const contactLabel = getContactLabel(contact);
                const formattedMailingAddress =
                  formatMailingAddress(contact.mailing_address) ||
                  contact.address;
                const hasMailingAddress = Boolean(contact.mailing_address);

                return (
                  <div
                    key={idx}
                    className={`rounded-lg border p-4 transition ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50/50'
                        : 'border-zinc-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="wizard-premises-contact"
                        id={`wizard-contact-${idx}`}
                        checked={isSelected}
                        onChange={() => setSelectedContactIdx(idx)}
                        disabled={isSubmitting}
                        className="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 space-y-2">
                        <label
                          htmlFor={`wizard-contact-${idx}`}
                          className="block cursor-pointer"
                        >
                          <p className="text-sm font-semibold text-zinc-800">
                            {contactLabel}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {getContactTypeLabel(contact)}
                            {formattedMailingAddress &&
                              ` • ${formattedMailingAddress}`}
                          </p>
                        </label>
                        {!hasMailingAddress && (
                          <p className="text-xs text-amber-600 mt-1">
                            Add a mailing address before this contact can be
                            used for certified mail.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {propertyContacts.length === 0 && (
                <p className="text-sm text-zinc-500 py-4 text-center">
                  No property contacts have been added yet. Add contacts in the
                  Research Property step.
                </p>
              )}
            </div>
          </div>
        );

      case 2: {
        const lorLabel =
          selectedLorDoc?.document_template?.name ||
          'Letter of Representation';
        const lorDate = selectedLorDoc
          ? new Date(selectedLorDoc.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '';
        const contactLabel = selectedContact
          ? getContactLabel(selectedContact)
          : 'Contact';
        const contactAddress =
          selectedContact?.address ||
          formatMailingAddress(selectedContact?.mailing_address);

        return (
          <div className="space-y-4">
            <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-4 space-y-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Letter of Representation
                </p>
                <p className="text-sm font-semibold text-zinc-900 mt-1">
                  {lorLabel}
                </p>
                <p className="text-xs text-zinc-500">{lorDate}</p>
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Certified Mail Recipient
                </p>
                <p className="text-sm font-semibold text-zinc-900 mt-1">
                  {contactLabel}
                </p>
                {contactAddress && (
                  <p className="text-xs text-zinc-500">{contactAddress}</p>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-4 py-3">
              <p className="text-sm text-amber-800">
                Nothing will be mailed yet. The certified-mail package will be
                prepared for the managing paralegal to review on the Review &
                Send step.
              </p>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const renderFooter = () => {
    return (
      <div className="flex items-center justify-between">
        <div>
          {currentStep > 0 && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {currentStep < 2 ? (
            <Button onClick={handleContinue} disabled={isSubmitting}>
              Continue
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                'Submit for Internal Review'
              )}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <StepDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Submit for Review"
      description={STEP_DESCRIPTIONS[currentStep]}
      steps={[...WIZARD_STEPS]}
      currentStep={currentStep}
      completedSteps={completedSteps}
      onStepClick={handleStepClick}
      footer={renderFooter()}
      preventClose={isSubmitting}
    >
      {renderStepContent()}
    </StepDialog>
  );
};
